import appManager from '@app/appManager';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import AppModule from '@app/lib/AppModule';
import { ITask, TaskState } from '@modules/TasksModule/types';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import {
  ServiceSessionCreatedEvent,
  ServiceSessionEvent,
  ServiceSessionEventType,
  ServiceSessionOfferedEvent,
  ServiceSessionUserEvent,
  ServiceSessionRecoveryEvent,
} from '@modules/ServiceModule/workspace/types/ServiceEvents';
import handleSessionDelivered from './events/handleSessionDelivered';
import handleSessionDeleted from './events/handleSessionDeleted';
import handleSessionCreated from './events/handleSessionCreated';
import handleSessionRecoveryStarted from './events/handleSessionRecoveryStarted';
import handleSessionRecoveryCompleted from './events/handleSessionRecoveryCompleted';
import handleSessionRescinded from './events/handleSessionRescinded';
import handleSessionRejected from './events/handleSessionRejected';
import handleSessionHangup from './events/handleSessionHangup';
import {
  CallEvent,
  PhonePrepareOutgoingEvent,
  WebphoneCallEvent,
} from '@modules/SoftPhoneModule/types';
import handleCallAnswered from './events/handleCallAnswered';
import handleCallMissed from './events/handleCallMissed';
import handleCallEnded from './events/handleCallEnded';
import { inboxState, inboxActions, resetInboxState } from './state';
import { InboxContext } from './types';
import handleTaskAccept from './events/handleTaskAccept';
import handleTaskReject from './events/handleTaskReject';
import handleTaskComplete from './events/handleTaskComplete';
import handleTaskPrepareOutbound from './events/handleTaskPrepare';
import handleTaskUnhold from './events/handleTaskUnhold';
import handleCallFailed from './events/handleCallFailed';
import handleCallFailedGeneral from './events/handleCallFailedGeneral';
import handleTaskTransfer from './events/handleTaskTransfer';

import handleSessionOffered from './events/handleSessionOffered';
import handleCallPlaced from './events/handleCallPlaced';
import TasksModule from '@modules/TasksModule';
import ServiceSession from '@modules/ServiceModule/workspace/types/ServiceSession';
import { tasksState } from '@modules/TasksModule/state';
import {
  CallRecordingSettingsOptions,
  ConversationTrait,
} from '@api/generated/graphql';
import Task from '@modules/TasksModule/Task';
import searchCustomers from '@components/CustomerSearch/data/searchCustomers';
import maskPhoneNumber from '@lib/utils/maskPhoneNumber';
import getDisplayName from '@lib/utils/getDisplayName';
import getRecordingStatus from './lib/getRecordingStatus';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import ServiceModule from '@modules/ServiceModule';
import textTaskType from './taskTypes/textTaskType';
import voiceTaskType from './taskTypes/voiceTaskType';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';
import { PcsConversationDetailsResult } from '@api/queries/getPcsConversationDetails';
import handleCallDetailsFetched from './events/handleCallDetailsFetched';
import { isTenantId } from '@lib/utils/tenant';

export class InboxModule extends AppModule {
  override events = [
    'user.status.initialized',
    'phone.pcs.conversation.conversationdetails.fetched',
  ];
  private userId: string | null = null;
  override preload(): void {
    // register task definitions
    TasksModule.registerTaskDefinition(textTaskType);
    TasksModule.registerTaskDefinition(voiceTaskType);
    // FFs load too late to use to decide which task type to register
    // so we'll just not load the V1 task type
    // TasksModule.registerTaskDefinition(voiceV1TaskType);

    // auth handlers
    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      this.userId = user.id;
      resetInboxState();
    });
    this.app.events.on(AuthEvents.SIGNOUT, () => {
      this.userId = null;
      resetInboxState();
    });

    // task handlers
    this.app.events.on('task.action.accept', (task: ITask) =>
      handleTaskAccept(this.context, task),
    );
    this.app.events.on('task.action.reject', (task: ITask) =>
      handleTaskReject(this.context, task),
    );
    this.app.events.on('task.action.complete', (task: ITask) => {
      handleTaskComplete(this.context, task, false);
    });
    this.app.events.on('task.action.complete-classified', (task: ITask) =>
      handleTaskComplete(this.context, task, true),
    );
    this.app.events.on('task.action.unhold', (task: ITask) =>
      handleTaskUnhold(this.context, task),
    );
    this.app.events.on('task.action.transfer', (task: ITask) => {
      handleTaskTransfer(this.context, task);
    });

    // service session handlers
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.DELIVERED,
      (ev) => handleSessionDelivered(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.REJECTED,
      (ev) => handleSessionRejected(this.context, ev),
    );
    this.app.events.on<ServiceSessionOfferedEvent>(
      ServiceSessionEventType.OFFERED,
      (ev) => {
        handleSessionOffered(this.context, ev);
      },
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.RESCINDED,
      (ev) => handleSessionRescinded(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.HANGUP,
      (ev) => handleSessionHangup(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.RECOVERY_COMPLETED,
      (ev) => handleSessionRecoveryCompleted(this.context, ev),
    );
    this.app.events.on<ServiceSessionRecoveryEvent>(
      ServiceSessionEventType.RECOVERY_STARTED,
      (ev) => handleSessionRecoveryStarted(this.context, ev),
    );
    this.app.events.on<ServiceSessionCreatedEvent>(
      ServiceSessionEventType.CREATED,
      (ev) => handleSessionCreated(this.context, ev),
    );
    this.app.events.on<ServiceSessionEvent>(
      ServiceSessionEventType.DELETED,
      (ev) => handleSessionDeleted(this.context, ev),
    );

    // phone call handlers
    this.app.events.on('phone.call.answered', (ev: CallEvent) =>
      handleCallAnswered(this.context, ev),
    );
    this.app.events.on('phone.call.ended', (ev: CallEvent) =>
      handleCallEnded(this.context, ev),
    );
    this.app.events.on('phone.call.placed', (ev: CallEvent) => {
      handleCallPlaced(this.context, ev);
    });
    this.app.events.on('phone.call.missed', (ev: CallEvent) =>
      handleCallMissed(this.context, ev),
    );

    this.app.events.on('phone.call.failed', (ev: WebphoneCallEvent) =>
      handleCallFailed(this.context, ev),
    );

    // SIP call-failed handler (CCPE-425). Drives inbound queue task transitions
    // by SIP reason code; flag-gated, no-op when the flag is OFF. Not added to
    // the `events` array because that array registers events this module EMITS —
    // here we only listen (same as phone.call.missed above).
    this.app.events.on('phone.call.failed-general', (ev: WebphoneCallEvent) =>
      handleCallFailedGeneral(this.context, ev),
    );

    this.app.events.on(
      'phone.pcs.conversation.conversationdetails.fetched',
      ({
        callId,
        conversationDetails,
      }: {
        callId: string;
        conversationDetails: PcsConversationDetailsResult;
      }) => {
        handleCallDetailsFetched(this.context, callId, conversationDetails);
      },
    );

    this.app.events.on<PhonePrepareOutgoingEvent>(
      'phone.prepare.outbound',
      (eventData) => handleTaskPrepareOutbound(this.context, eventData),
    );

    // user status handlers
    this.app.events.on('userStatus.updated.available', this.resetMissedCalls);

    this.app.events.on('user.status.initialized', () => {
      this.handleInProgressTransferTasksOnLoad();
    });
  }

  /**
   * Goes through all tasks and calls the transfer handler
   * for each task that has an inProgress transfer status.
   *
   * This is used to handle the transfer tasks that were in progress
   * at the time of the app refresh.
   */
  private handleInProgressTransferTasksOnLoad = () => {
    const featureInboxCallWarmTransfers = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureInboxCallWarmTransfers,
    );
    if (!featureInboxCallWarmTransfers) {
      TasksModule.getTasks().forEach((task) => {
        if (task.attributes.transferStatus?.inProgress) {
          handleTaskTransfer(this.context, task);
        }
      });
    }
  };

  public setSelectedTaskId(id: string | undefined) {
    inboxActions.setSelectedTaskId(id);
  }

  private get context(): InboxContext {
    if (!this.userId) {
      throw new Error('User not signed in');
    }

    return {
      userId: this.userId,
      logger: this.app.logger,
      parent: this,
    };
  }

  public handleMissedCallsForQueue = (queueId: string) => {
    inboxState.missedCallsForQueue[queueId] = inboxState.missedCallsForQueue[
      queueId
    ]
      ? inboxState.missedCallsForQueue[queueId] + 1
      : 1;
  };

  public handleMissedDidCall = () => {
    inboxState.missedCallsDid += 1;
  };

  public resetMissedCallsDid = () => {
    inboxState.missedCallsDid = 0;
  };

  public resetMissedQueueCalls = () => {
    inboxState.missedCallsForQueue = {};
  };

  public resetMissedCalls = () => {
    this.resetMissedQueueCalls();
    this.resetMissedCallsDid();
  };

  public showClassificationModalForTaskId = (taskId: string) => {
    if (taskId) {
      ModalsModule.openModal(ModalId.CallClassification, {
        open: true,
        taskId: taskId,
      });
    } else {
      ModalsModule.closeActiveModal();
    }
  };

  public setSessionProperties = (
    taskId: string,
    session: Pick<
      ServiceSession,
      | 'conversationId'
      | 'conversationSid'
      | 'queueId'
      | 'queueName'
      | 'queueEnterTime'
      | 'queueTimeout'
      | 'tenantId'
    >,
    to: string,
  ) => {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    TasksModule.setTaskAttribute(taskId, 'sessionId', session.conversationId);
    TasksModule.setTaskAttribute(taskId, 'sessionSid', session.conversationSid);
    if (session.queueTimeout) {
      TasksModule.setTaskAttribute(
        taskId,
        'queueTimeoutAt',
        session.queueTimeout,
      );
    }
    this.setTenantIdForTask(task, session.tenantId);
    task.queueId = session.queueId;
    task.queueName = session.queueName;
    task.to = to;
  };

  public setTenantIdForTask = (task: Task, tenantId: string) => {
    if (task.tenantId === tenantId) {
      return;
    }

    if (!isTenantId(tenantId)) {
      return;
    }

    task.tenantId = tenantId;

    // search for the customer, set the customer on the task if we find a single match
    searchCustomers(
      {
        phone: maskPhoneNumber(getDisplayName(task.from, {})),
      },
      tenantId,
    ).then((results) => {
      if (results.customers.length === 1) {
        const customer = results.customers[0];
        TasksModule.setCustomerOnTask(task.id, {
          id: customer.id,
          name: customer.name,
        });
      }
    });
  };

  public evaluateAndSetTaskType(taskId: string) {
    const task = TasksModule.getTask(taskId);

    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }

    const hasSession = ServiceModule.hasUserSession(
      task.attributes.sessionSid,
      this.userId ?? '',
    );
    const isExternal =
      task.attributes.traits?.includes(ConversationTrait.External) ?? false;
    const isDirectToUser =
      task.attributes.traits?.includes(ConversationTrait.DirectToUser) ?? false;
    const hasTraitsSet = task.attributes.traits !== undefined;
    const isFromInternalUser = task.from.startsWith('User');

    // When RTCS is down (no session), use task.queueId set from PCS as a fallback
    // queue signal so that queue calls are not misclassified as DID calls.
    // DirectToUser calls are excluded because they bypass the queue entirely.
    const isRtcsDegradedQueueDetection = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureRtcsDegradedQueueDetection,
    );
    const isQueueCall =
      hasSession ||
      (isRtcsDegradedQueueDetection && !!task.queueId && !isDirectToUser);

    const updateRecordInteraction = (value: boolean) => {
      // skip setting recordInteraction if the task is already missed
      if (
        task.state !== TaskState.TimedOut &&
        task.state !== TaskState.Abandoned
      ) {
        TasksModule.setTaskAttribute(taskId, 'recordInteraction', value);
      }
    };

    // Normalize the "is external caller" signal:
    // When traits are set, use the External trait directly.
    // When traits are absent (PCS conversation not yet returned or errored),
    // fall back to !isFromInternalUser.
    const isExternalCaller = hasTraitsSet ? isExternal : !isFromInternalUser;

    if (isQueueCall) {
      if (isExternalCaller) {
        task.type = 'voice-external-queue';
        TasksModule.setTaskAttribute(taskId, 'shouldClassify', true);
        updateRecordInteraction(true);
      } else {
        task.type = 'voice-internal-queue';
        TasksModule.setTaskAttribute(taskId, 'shouldClassify', false);
      }
    } else if (isExternalCaller) {
      // For DID calls, only classify if traits confirm it's not a direct-to-user call.
      // When traits are absent, skip classification conservatively.
      const shouldClassify = hasTraitsSet && !isDirectToUser;
      task.type = 'voice-external-did';
      TasksModule.setTaskAttribute(taskId, 'shouldClassify', shouldClassify);
      if (shouldClassify) {
        updateRecordInteraction(true);
      }
    } else if (hasTraitsSet) {
      // hasTraitsSet=true, isExternalCaller=false, isQueueCall=false → internal DID call.
      // Note: the unresolved case (!hasTraitsSet && !isQueueCall && isFromInternalUser) falls
      // through all three branches unchanged — task.type is not set, matching original behavior.
      task.type = 'voice-internal-did';
      TasksModule.setTaskAttribute(taskId, 'shouldClassify', false);
    }
  }

  public isDid(task: Task): boolean {
    return (
      task.type === 'voice-external-did' || task.type === 'voice-internal-did'
    );
  }

  public hasSession(task: Task): boolean {
    return !!task.attributes.sessionId;
  }

  public fetchRecordingStatus = async (
    kazooCallId?: string,
    callId?: string,
    delayedRequery?: boolean,
  ) => {
    if (!callId || !kazooCallId) {
      this.app.logger.error('CallId not provided for fetchRecordingStatus');
      return;
    }
    // We re-query the recording status because if a call is transferred and uses the destination's recording settings,
    // PCS does not have the data ready, so the recording status fetches the transferer's call settings instead of the transferee's.
    if (delayedRequery) {
      const delayedRequestTimes = [1000, 3000, 6000];
      for (const delay of delayedRequestTimes) {
        setTimeout(() => {
          this.updateSoftPhoneModuleRecordingState(kazooCallId, callId);
        }, delay);
      }
    } else {
      this.updateSoftPhoneModuleRecordingState(kazooCallId, callId);
    }
  };

  private async updateSoftPhoneModuleRecordingState(
    kazooCallId: string,
    callId: string,
  ) {
    getRecordingStatus(kazooCallId).then((recordingStatus) => {
      if (
        !recordingStatus ||
        recordingStatus.settings === CallRecordingSettingsOptions.Unset
      ) {
        this.app.logger.warn(
          'Unable to fetch a Recording for call',
          callId,
          recordingStatus,
        );
        return;
      }
      SoftPhoneModule.setRecordingState(callId, recordingStatus);
    });
  }
}

export default new InboxModule(appManager.events, appManager.logger('Inbox'));
